import { useQuery } from '@tanstack/react-query';
import supabaseClient from '@/api/supabaseClient.ts';
import { useAuthContext } from '@/context/AuthContext.tsx';
import type { Json } from '$/database.types';
import {
  parseStructuredSummary,
  resolveDisplayStatus,
  type SessionDisplayStatus,
} from '@/utils/sessionTableDisplay.ts';

type DecisionRow = {
  id: string;
  issue_title: string | null;
  status: string;
  primary_framework: string | null;
  governance_score: number | null;
  created_at: string;
};

type LedgerEntryRow = {
  decision_id: string;
  step: string;
  ai_output: Json | null;
  created_at: string;
};

type ApprovalStateRow = {
  decision_id: string;
  status: string;
  created_at: string;
};

export type OrgDecisionRow = {
  id: string;
  issue_title: string | null;
  lifecycle_status: string;
  primary_framework: string | null;
  governance_score: number | null;
  structured_summary: string | null;
  last_activity_at: string;
  display_status: SessionDisplayStatus;
  pending_review: boolean;
};

const PENDING_APPROVAL_STATUSES = new Set(['pending', 'under_review']);

function mapDecisionRow(
  row: DecisionRow,
  ledgerByDecision: Map<string, LedgerEntryRow[]>,
  approvalByDecision: Map<string, ApprovalStateRow[]>,
): OrgDecisionRow {
  const ledgerEntries = ledgerByDecision.get(row.id) ?? [];
  const intentLedger = ledgerEntries
    .filter((e) => e.step === 'intent_captured')
    .sort((a, b) => b.created_at.localeCompare(a.created_at))[0];

  const structured_summary = parseStructuredSummary(intentLedger?.ai_output);

  const approvalStates = approvalByDecision.get(row.id) ?? [];
  const pending_review = approvalStates.some((a) => PENDING_APPROVAL_STATUSES.has(a.status));

  const last_activity_at = [
    row.created_at,
    ...ledgerEntries.map((e) => e.created_at),
    ...approvalStates.map((a) => a.created_at),
  ]
    .sort((a, b) => b.localeCompare(a))[0];

  const display_status = resolveDisplayStatus({
    lifecycleStatus: row.status,
    governanceScore: row.governance_score,
    pendingReview: pending_review,
  });

  return {
    id: row.id,
    issue_title: row.issue_title,
    lifecycle_status: row.status,
    primary_framework: row.primary_framework,
    governance_score: row.governance_score,
    structured_summary,
    last_activity_at,
    display_status,
    pending_review,
  };
}

async function fetchOrgDecisions(orgId: string): Promise<OrgDecisionRow[]> {
  const { data: decisions, error } = await supabaseClient
    .from('decisions')
    .select('id, issue_title, status, primary_framework, governance_score, created_at')
    .eq('org_id', orgId)
    .order('created_at', { ascending: false });

  if (error) throw new Error(error.message);

  const rows = (decisions ?? []) as DecisionRow[];
  if (rows.length === 0) return [];

  const ids = rows.map((r) => r.id);

  const [{ data: ledgerRows }, { data: approvalRows }] = await Promise.all([
    supabaseClient
      .from('ledger_entries')
      .select('decision_id, step, ai_output, created_at')
      .in('decision_id', ids),
    supabaseClient
      .from('approval_states')
      .select('decision_id, status, created_at')
      .in('decision_id', ids),
  ]);

  const ledgerByDecision = new Map<string, LedgerEntryRow[]>();
  for (const entry of (ledgerRows ?? []) as LedgerEntryRow[]) {
    const list = ledgerByDecision.get(entry.decision_id) ?? [];
    list.push(entry);
    ledgerByDecision.set(entry.decision_id, list);
  }

  const approvalByDecision = new Map<string, ApprovalStateRow[]>();
  for (const entry of (approvalRows ?? []) as ApprovalStateRow[]) {
    const list = approvalByDecision.get(entry.decision_id) ?? [];
    list.push(entry);
    approvalByDecision.set(entry.decision_id, list);
  }

  return rows.map((row) => mapDecisionRow(row, ledgerByDecision, approvalByDecision));
}

const useOrgDecisions = () => {
  const { userProfile } = useAuthContext();
  const orgId = userProfile?.org_id;

  return useQuery({
    queryKey: ['decisions', orgId],
    queryFn: () => fetchOrgDecisions(orgId!),
    enabled: !!orgId,
  });
};

export default useOrgDecisions;
